const SAM_LEGAL_NAME_ALIASES = ['Legal_Business_Name', 'legal_business_name', 'legalBusinessName', 'legal_business'];

const SAM_EMAIL_ALIASES = [
  'Accounts_Receivable_POC_Email',
  'accounts_receivable_poc_email',
  'accountsReceivablePocEmail',
  'email',
  'Email',
  'poc_email',
  'POC_Email',
];

const SAM_WEBSITE_ALIASES = [
  'Corporate_Website_URL',
  'corporate_website_url',
  'Corporate_Website',
  'corporate_website',
  'website',
  'Web_Site',
  'web_site',
];

const SAM_PSC_ALIASES = [
  'PSC',
  'psc',
  'psc_codes',
  'PSC_Codes',
  'Product_Service_Codes',
  'product_service_codes',
  'primary_psc',
  'Primary_PSC',
];

const SAM_PRIMARY_NAICS_ALIASES = [
  'Primary_NAICS',
  'primary_naics',
  'what_is_the_companys_primary_six_6_digit_naics_code',
];

const SAM_SECONDARY_NAICS_ALIASES = [
  'Secondary_NAICS',
  'secondary_naics',
  'list_any_secondary_naics_codes_or_provide_a_detailed_description_of_what_your_company_provides',
];

const SAM_ADDRESS1_ALIASES = ['Physical_Address', 'physical_address', 'address', 'Address', 'addressLine1'];

const SAM_ADDRESS2_ALIASES = ['Physical_Address_Line_2', 'physical_address_line_2', 'address2', 'Address_2'];

const SAM_CITY_ALIASES = ['Physical_Address_City', 'physical_address_city', 'city', 'City'];

const SAM_STATE_ALIASES = ['Physical_Address_State', 'physical_address_state', 'state', 'State'];

const SAM_ZIP_ALIASES = ['Physical_Address_Zip', 'Physical_Address_ZIP', 'physical_address_zip', 'zip', 'Zip'];

const SAM_COUNTRY_ALIASES = ['Physical_Address_Country', 'physical_address_country', 'country', 'Country'];

const SAM_PHONE_ALIASES = [
  'Accounts_Receivable_POC_Phone',
  'accounts_receivable_poc_phone',
  'accountsReceivablePocPhone',
  'phone',
  'Phone',
  'phoneNumber',
];

const SAM_UEI_ALIASES = ['Uei', 'uei', 'ueiCode', 'UEI'];

const SAM_CAGE_ALIASES = ['Cage', 'cage', 'cageCode'];

const SAM_DBA_ALIASES = ['DBA', 'dba', 'doing_business_as'];

const SAM_POC_NAME_ALIASES = [
  'Accounts_Receivable_POC_Name',
  'accounts_receivable_poc_name',
  'poc_name',
  'point_of_contact_name',
];

export function parseFormPartial(raw) {
  if (raw == null) return {};
  if (typeof raw === 'object' && !Array.isArray(raw)) return { ...raw };
  if (typeof raw === 'string') {
    try {
      const p = JSON.parse(raw);
      return p && typeof p === 'object' && !Array.isArray(p) ? p : {};
    } catch {
      return {};
    }
  }
  return {};
}

function pickAliased(samFlat, aliases) {
  if (!samFlat || typeof samFlat !== 'object') return undefined;
  for (const key of aliases) {
    const v = samFlat[key];
    if (v == null) continue;
    if (typeof v === 'string' && v.trim() === '') continue;
    return typeof v === 'string' ? v.trim() : v;
  }
  return undefined;
}

function splitToLinesOrComma(value) {
  if (value == null) return [];
  const s = String(value).trim();
  if (!s) return [];
  return s
    .split(/[\n,;]+/)
    .map((x) => x.trim())
    .filter(Boolean);
}

/**
 * Map raw SAM submission keys to shapes aligned with organization manual registration / details.
 * @param {Record<string, unknown>} samFlat
 * @returns {Record<string, unknown>}
 */
export function deriveSamPrefillDetails(samFlat) {
  if (!samFlat || typeof samFlat !== 'object') return {};

  const legal = pickAliased(samFlat, SAM_LEGAL_NAME_ALIASES);
  const primaryNaicsStr = pickAliased(samFlat, SAM_PRIMARY_NAICS_ALIASES);
  const secondaryNaicsStr = pickAliased(samFlat, SAM_SECONDARY_NAICS_ALIASES);
  const pscStr = pickAliased(samFlat, SAM_PSC_ALIASES);

  const primaryNaics = primaryNaicsStr != null ? splitToLinesOrComma(primaryNaicsStr) : undefined;
  const secondaryNaics = secondaryNaicsStr != null ? splitToLinesOrComma(secondaryNaicsStr) : undefined;
  const pscCodes = pscStr != null ? splitToLinesOrComma(pscStr) : undefined;

  const addressLine1 = pickAliased(samFlat, SAM_ADDRESS1_ALIASES);
  const pocName = pickAliased(samFlat, SAM_POC_NAME_ALIASES);

  return {
    companyName: legal,
    legalBusinessName: legal,
    ueiCode: pickAliased(samFlat, SAM_UEI_ALIASES),
    cageCode: pickAliased(samFlat, SAM_CAGE_ALIASES),
    dba: pickAliased(samFlat, SAM_DBA_ALIASES),
    addressLine1,
    physicalAddress: addressLine1,
    addressLine2: pickAliased(samFlat, SAM_ADDRESS2_ALIASES),
    city: pickAliased(samFlat, SAM_CITY_ALIASES),
    state: pickAliased(samFlat, SAM_STATE_ALIASES),
    zipCode: pickAliased(samFlat, SAM_ZIP_ALIASES),
    country: pickAliased(samFlat, SAM_COUNTRY_ALIASES),
    phoneNumber: pickAliased(samFlat, SAM_PHONE_ALIASES),
    website: pickAliased(samFlat, SAM_WEBSITE_ALIASES),
    contactEmail: pickAliased(samFlat, SAM_EMAIL_ALIASES),
    primaryNaics,
    secondaryNaics,
    pscCodes,
    accountsReceivablePocName: pocName,
  };
}

const isEmpty = (v) => v == null || (typeof v === 'string' && v.trim() === '') || (Array.isArray(v) && v.length === 0);

/**
 * Fill only blank manual registration fields from SAM-derived values.
 * @param {{ generalInformationData: object, companyAddressData: object, industryData: object }} prev
 * @param {Record<string, unknown>} samDerived
 */
export function mergeManualRegistrationFromSam(prev, samDerived) {
  if (!samDerived || typeof samDerived !== 'object') return prev;

  const nextGeneral = { ...prev.generalInformationData };
  const generalPairs = [
    ['companyName', 'companyName'],
    ['ueiCode', 'ueiCode'],
    ['cageCode', 'cageCode'],
    ['dba', 'dba'],
    ['corporateUrl', 'website'],
    ['phone', 'phoneNumber'],
  ];
  for (const [target, samKey] of generalPairs) {
    if (isEmpty(nextGeneral[target]) && !isEmpty(samDerived[samKey])) {
      nextGeneral[target] = samDerived[samKey];
    }
  }

  const nextAddr = { ...prev.companyAddressData };
  const addrPairs = [
    ['addressLine1', 'addressLine1'],
    ['addressLine2', 'addressLine2'],
    ['city', 'city'],
    ['state', 'state'],
    ['zipCode', 'zipCode'],
    ['country', 'country'],
  ];
  for (const [target, samKey] of addrPairs) {
    if (isEmpty(nextAddr[target]) && !isEmpty(samDerived[samKey])) {
      nextAddr[target] = samDerived[samKey];
    }
  }

  const nextIndustry = { ...prev.industryData };
  if (isEmpty(nextIndustry.primaryNaics) && Array.isArray(samDerived.primaryNaics) && samDerived.primaryNaics.length) {
    nextIndustry.primaryNaics = samDerived.primaryNaics;
  }
  if (
    isEmpty(nextIndustry.secondaryNaics) &&
    Array.isArray(samDerived.secondaryNaics) &&
    samDerived.secondaryNaics.length
  ) {
    nextIndustry.secondaryNaics = samDerived.secondaryNaics;
  }
  if (isEmpty(nextIndustry.pscCodes) && Array.isArray(samDerived.pscCodes) && samDerived.pscCodes.length) {
    nextIndustry.pscCodes = samDerived.pscCodes;
  }

  return {
    generalInformationData: nextGeneral,
    companyAddressData: nextAddr,
    industryData: nextIndustry,
  };
}
